import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { DUR, EASE_OUT_QUINT } from '@/lib/motion';

const WORDMARK = 'Slobozip';

/**
 * Sign-off — the closing frame.
 *
 * Drenched plate (full-bleed coral, light theme) carrying the wordmark
 * at display scale. Colophon below: typography credits, build version,
 * license, contact. Easter egg line: the site's compressed self-stat.
 */
export function Signoff() {
  const ref = useRef<HTMLHeadingElement>(null);
  const inView = useInView(ref, { once: true, margin: '-15%' });

  return (
    <section
      id="signoff"
      className="relative overflow-hidden"
      style={{
        background: 'var(--plate-bg)',
        color: 'var(--plate-text)',
        paddingTop: 120,
        paddingBottom: 56,
      }}
    >
      <div className="container-x relative">
        {/* Cinematic wordmark — letter-by-letter rise, Instrument Serif italic for "zip" */}
        <div style={{ position: 'relative', display: 'inline-block' }}>
        <h2
          ref={ref}
          aria-label={WORDMARK}
          className="flex flex-wrap items-end"
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--type-display)',
            fontWeight: 600,
            letterSpacing: 'var(--tracking-display)',
            lineHeight: 0.85,
          }}
        >
          {WORDMARK.split('').map((ch, i) => {
            const isItalicTail = i >= 4; // "zip"
            return (
              <motion.span
                key={i}
                aria-hidden
                initial={{ y: 96, opacity: 0 }}
                animate={inView ? { y: 0, opacity: 1 } : { y: 96, opacity: 0 }}
                transition={{
                  duration: DUR.slower,
                  delay: i * 0.05,
                  ease: EASE_OUT_QUINT,
                }}
                style={{
                  display: 'inline-block',
                  fontFamily: isItalicTail ? 'var(--font-serif)' : 'var(--font-sans)',
                  fontStyle: isItalicTail ? 'italic' : 'normal',
                  fontWeight: isItalicTail ? 400 : 600,
                  color: 'var(--plate-text)',
                }}
              >
                {ch}
              </motion.span>
            );
          })}
        </h2>
        {/* Coral underline — draws left → right after the wordmark stagger
         *  finishes (last letter at ~0.35s × 1 + 1.4s duration; we start
         *  at 1.4s so the bar lands as the letters settle). */}
        <motion.span
          aria-hidden
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: 0.9, delay: 1.4, ease: EASE_OUT_QUINT }}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: -6,
            height: 3,
            background: 'var(--accent)',
            transformOrigin: 'left',
            borderRadius: 2,
            boxShadow: '0 0 16px var(--accent-glow)',
          }}
        />
        </div>

        {/* Colophon grid */}
        <div
          className="mt-20 grid gap-y-12"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            columnGap: 48,
            borderTop: '1px solid color-mix(in oklch, var(--plate-text) 20%, transparent)',
            paddingTop: 32,
          }}
        >
          <Column label="Author">
            <p style={{ fontWeight: 500 }}>Marko Slobođan</p>
            <a
              href="mailto:marko.slobodjan6@gmail.com"
              className="t-mono"
              style={{
                fontSize: 'var(--type-xs)',
                color: 'color-mix(in oklch, var(--plate-text) 75%, transparent)',
                marginTop: 6,
                display: 'inline-block',
              }}
            >
              marko.slobodjan6@gmail.com
            </a>
          </Column>

          <Column label="Project">
            <Link href="https://github.com/MarkoSlobodjan/slobozip" external>
              GitHub
            </Link>
            <Link href="https://github.com/MarkoSlobodjan/slobozip/releases" external>
              Releases
            </Link>
            <Link href="https://github.com/MarkoSlobodjan/slobozip/blob/main/LICENSE" external>
              MIT license
            </Link>
          </Column>

          <Column label="Typography">
            <p style={{ fontFamily: 'var(--font-sans)', fontWeight: 500 }}>Geist · Geist Mono</p>
            <p
              style={{
                fontFamily: 'var(--font-serif)',
                fontStyle: 'italic',
                fontSize: 'var(--type-md)',
                marginTop: 4,
              }}
            >
              Instrument Serif
            </p>
          </Column>

          <Column label="Build">
            <p className="t-mono" style={{ fontSize: 'var(--type-xs)' }}>
              v0.3.0
            </p>
            <p
              className="t-mono"
              style={{
                fontSize: 'var(--type-xs)',
                color: 'color-mix(in oklch, var(--plate-text) 75%, transparent)',
                marginTop: 4,
              }}
            >
              C++17 · WebAssembly
            </p>
          </Column>
        </div>

        {/* Easter-egg line */}
        <p
          className="t-mono mt-20"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'color-mix(in oklch, var(--plate-text) 70%, transparent)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
          }}
        >
          // this site weighs less than it should · compressed by sloboZIP itself
        </p>
      </div>
    </section>
  );
}

function Column({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col" style={{ gap: 6 }}>
      <span
        className="t-mono"
        style={{
          fontSize: 'var(--type-xs)',
          color: 'color-mix(in oklch, var(--plate-text) 60%, transparent)',
          letterSpacing: 'var(--tracking-mono-caps)',
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

function Link({
  href,
  children,
  external = false,
}: {
  href: string;
  children: React.ReactNode;
  external?: boolean;
}) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      style={{
        fontSize: 'var(--type-sm)',
        color: 'var(--plate-text)',
        textDecoration: 'underline',
        textDecorationColor: 'color-mix(in oklch, var(--plate-text) 30%, transparent)',
        textUnderlineOffset: 4,
        textDecorationThickness: 1,
        transition: 'text-decoration-color var(--dur-fast) var(--ease-out-expo)',
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.textDecorationColor = 'var(--plate-text)')
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.textDecorationColor =
          'color-mix(in oklch, var(--plate-text) 30%, transparent)')
      }
    >
      {children}
      {external && ' ↗'}
    </a>
  );
}
